import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  useTheme,
  Box,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  DialogActions,
} from "@material-ui/core";
import {
  InfoOutlined,
  FileCopy as CopyIcon,
  Check as CheckIcon,
} from "@material-ui/icons";
import TwilioTemplateWizard from "../TwilioTemplateWizard";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const maskSid = (sid) => {
  if (!sid) return "";
  return sid.substring(0, 4) + "..." + sid.substring(sid.length - 4);
};

const PURPOSES = ["recordatorio", "confirmada", "modificada", "cancelada"];

const TwilioInstructionsModal = ({ open, onClose, accountSids = [] }) => {
  const theme = useTheme();
  const [copied, setCopied] = useState(false);
  const [selectedSid, setSelectedSid] = useState(accountSids[0] || "");
  const [confirmExit, setConfirmExit] = useState(false);
  const [completenessMap, setCompletenessMap] = useState({});
  const [loadingCompleteness, setLoadingCompleteness] = useState(false);

  const webhookUrl = process.env.REACT_APP_BACKEND_URL + "/twilio/incoming";

  useEffect(() => {
    if (!open) return;
    const firstSid = accountSids[0] || "";
    setSelectedSid((prev) => (prev !== firstSid ? firstSid : prev));
  }, [open, accountSids]);

  useEffect(() => {
    if (!open) return;
    const fetchAllCompleteness = async () => {
      if (!accountSids || accountSids.length === 0) {
        setCompletenessMap({});
        return;
      }
      setLoadingCompleteness(true);
      const map = {};
      await Promise.all(
        accountSids.map(async (sid) => {
          try {
            const results = await Promise.all(
              PURPOSES.map(async (purpose) => {
                try {
                  const { data } = await api.get(`/twilio-templates/${sid}/${purpose}`);
                  return !!(data && data.hx_template && data.hx_template.trim().length > 0);
                } catch (err) {
                  return false;
                }
              })
            );
            map[sid] = results.every(Boolean);
          } catch (err) {
            map[sid] = false;
          }
        })
      );
      setCompletenessMap(map);
      setLoadingCompleteness(false);
    };

    fetchAllCompleteness().catch((err) => {
      console.error("Error fetching templates completeness", err);
      setLoadingCompleteness(false);
    });
  }, [open, accountSids]);

  const handleCopy = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExit = () => {
    const currentSid = selectedSid || accountSids[0] || null;
    if (!currentSid) {
      onClose();
      return;
    }

    const isComplete = completenessMap[currentSid];
    if (isComplete) {
      onClose();
      return;
    }

    setConfirmExit(true);
  };

  const confirmClose = () => {
    setConfirmExit(false);
    onClose();
  };

  const cancelClose = () => {
    setConfirmExit(false);
  };

  return (
    <>
      <Dialog open={open} onClose={handleExit} maxWidth="md" fullWidth>
        <DialogTitle
          style={{
            borderBottom: "1px solid " + theme.palette.divider,
          }}
        >
          <Box display="flex" alignItems="center">
            <InfoOutlined style={{ marginRight: 8, color: theme.palette.primary.main }} />
            Configuración de Twilio
          </Box>
        </DialogTitle>

        <DialogContent style={{ padding: 24 }}>
          <Typography variant="h6" gutterBottom style={{ fontSize: "1rem", fontWeight: 600 }}>
            Parte 1: Configuración de Incoming Messages
          </Typography>

          <Box mb={2}>
            <Typography variant="body1" paragraph>
              Twilio ahora gestiona los mensajes entrantes a través de un <strong>Messaging Service</strong>.
              Debés configurar el webhook para que los mensajes lleguen correctamente.
            </Typography>
          </Box>

          <Box bgcolor={theme.palette.action.hover} p={2} borderRadius={4} mb={3}>
            <Typography variant="subtitle2" style={{ fontWeight: "bold", marginBottom: 8 }}>
              URL del Webhook a configurar:
            </Typography>

            <Box display="flex" alignItems="center">
              <Typography
                style={{
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  padding: "10px",
                  border: "1px solid " + theme.palette.divider,
                  borderRadius: 4,
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                  fontWeight: "bold",
                  flexGrow: 1,
                  marginRight: 8,
                }}
              >
                {webhookUrl}
              </Typography>

              <Tooltip title={copied ? "Copiado!" : "Copiar URL"} placement="top">
                <IconButton onClick={handleCopy} color={copied ? "primary" : "default"}>
                  {copied ? <CheckIcon /> : <CopyIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <ol style={{ paddingLeft: 20, lineHeight: 1.8 }}>
            <li>
              <Typography variant="body2">
                Inicia sesión en Twilio y ve a <strong>Messaging</strong> {">"} <strong>Services</strong>.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">Crea un nuevo servicio (o selecciona uno existente).</Typography>
            </li>
            <li>
              <Typography variant="body2">En el menú lateral, ve a <strong>Integration</strong>.</Typography>
            </li>
            <li>
              <Typography variant="body2">
                En <strong>"Incoming Messages"</strong>, selecciona <strong>"Send a webhook"</strong>.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">Pega la <strong>URL del Webhook</strong> en <strong>Request URL</strong>.</Typography>
            </li>
            <li>
              <Typography variant="body2">Método: <strong>HTTP POST</strong>. Guarda los cambios.</Typography>
            </li>
            <li>
              <Typography variant="body2">En <strong>Sender Pool</strong>, agrega tu número de WhatsApp activo.</Typography>
            </li>
          </ol>

          <Typography variant="h6" gutterBottom style={{ fontSize: "1rem", fontWeight: 600, marginTop: 24 }}>
            Parte 2: Configuración de Plantillas
          </Typography>

          <Typography variant="body2" paragraph>
            Las plantillas HX son necesarias para enviar recordatorios y mensajes de citas.
            Si no configurás plantillas, el envío de mensajes no funcionará correctamente.
          </Typography>

          <Box mb={2}>
            <Button
              variant="outlined"
              color="primary"
              href="https://console.twilio.com/us1/develop/sms/content-template-builder"
              target="_blank"
              rel="noopener noreferrer"
            >
              Abrir Content Template Builder en Twilio
            </Button>
          </Box>

          {accountSids.length === 0 && (
            <Box mt={3}>
              <Typography color="error" variant="body2" paragraph>
                ⚠️ No hay cuentas Twilio configuradas. El envío de mensajes no funcionará hasta que agregues una.
              </Typography>
              <Box display="flex" justifyContent="flex-end">
                <Button onClick={onClose} variant="contained" color="primary">
                  Salir
                </Button>
              </Box>
            </Box>
          )}

          {accountSids.length === 1 && (
            <Box mt={4}>
              <TwilioTemplateWizard
                accountSid={accountSids[0]}
                onFinish={() => {
                  setCompletenessMap((prev) => ({ ...prev, [accountSids[0]]: true }));
                }}
              />
            </Box>
          )}

          {accountSids.length > 1 && (
            <Box mt={3}>
              <TextField
                select
                label="Selecciona la cuenta Twilio"
                value={selectedSid}
                onChange={(e) => setSelectedSid(e.target.value)}
                fullWidth
                variant="outlined"
              >
                {accountSids.map((sid) => (
                  <MenuItem key={sid} value={sid}>
                    {maskSid(sid)}
                  </MenuItem>
                ))}
              </TextField>

              {selectedSid && (
                <Box mt={4}>
                  <TwilioTemplateWizard
                    accountSid={selectedSid}
                    onFinish={() => {
                      setCompletenessMap((prev) => ({ ...prev, [selectedSid]: true }));
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleExit} color="secondary">
            Salir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmExit} onClose={cancelClose}>
        <DialogTitle>Confirmar salida</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Si salís sin configurar plantillas, los recordatorios y mensajes de citas no van a funcionar correctamente.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelClose} color="default">
            Cancelar
          </Button>
          <Button
            onClick={confirmClose}
            color="secondary"
            variant="contained"
          >
            Salir igualmente
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TwilioInstructionsModal;
